// src/views/words-lists/components/TreeContainer.tsx
/* eslint-disable max-len */
import React, { forwardRef, useRef } from 'react';
import { Tree, TreeApi, NodeRendererProps } from 'react-arborist';
import { Button, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FolderOutlined,
  SearchOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { TreeNodeType } from '../../../services/ApiService';
import styles from '../FolderTreeView.module.less';

interface TreeContainerProps {
  filteredTree: TreeNodeType[];
  debouncedSearchQuery: string;
  treeProps: any;
  renderNode: (props: NodeRendererProps<TreeNodeType>) => JSX.Element;
  isLoading: boolean;
  onResetSearch: () => void;
}

export const TreeContainer = forwardRef<TreeApi<TreeNodeType>, TreeContainerProps>(
  (props, ref) => {
    const {
      filteredTree, debouncedSearchQuery, treeProps, renderNode, isLoading, onResetSearch,
    } = props;
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    if (isLoading && filteredTree.length === 0) {
      return (
        <div className={styles.loadingContainer}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            tip={t('folders:tree.loading')}
          />
        </div>
      );
    }

    if (filteredTree.length === 0 && debouncedSearchQuery) {
      return (
        <div className={styles.emptyState}>
          <SearchOutlined style={{ fontSize: 48, opacity: 0.3 }} />
          <p>{t('folders:tree.noResults', { query: debouncedSearchQuery })}</p>
          <Button className={styles.btnClearSearch} onClick={onResetSearch}>{t('folders:tree.clearSearch')}</Button>
        </div>
      );
    }

    if (filteredTree.length === 0) {
      return (
        <div className={styles.emptyState}>
          <FolderOutlined style={{ fontSize: 48, opacity: 0.3 }} />
          <p>{t('common:emptyHere')}</p>
        </div>
      );
    }

    return (
      <div ref={wrapperRef} className={styles.treeWrapper} style={{ height: treeProps?.height ? `${treeProps.height}px` : '100%', minHeight: 0 }}>
        <Tree ref={ref} {...treeProps}>
          {renderNode}
        </Tree>
      </div>

    );
  },
);

TreeContainer.displayName = 'TreeContainer';
